import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch } from 'store/store';
import { fetchAchievementsThunk } from 'actions/gamificationActions';
import { getAchievements, getGamificationLoading } from 'store/selectors/gamification.selector';
import { getIsGamificationEnabled } from 'store/selectors/auth.selector';

import { PanelWrapper, PanelTitle, ProgressCardsColumn, ProgressCardRowDivider } from './styles';
import { AchievementListRow } from './AchievementListRow';

export const AchievementsPanel: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const gamificationEnabled = useSelector(getIsGamificationEnabled);
  const achievements = useSelector(getAchievements);
  const loading = useSelector(getGamificationLoading);

  useEffect(() => {
    if (!gamificationEnabled) return;

    dispatch(fetchAchievementsThunk());
  }, [dispatch, gamificationEnabled]);

  const displayList = useMemo(() => {
    const inProgress = achievements.filter((a) => !a.is_completed);
    const completed = achievements.filter((a) => a.is_completed);

    return [...inProgress, ...completed];
  }, [achievements]);

  if (!gamificationEnabled) {
    return null;
  }

  if (loading && displayList.length === 0) {
    return null;
  }

  if (displayList.length === 0) {
    return null;
  }

  return (
    <PanelWrapper>
      <PanelTitle>{t('gamification.panel.title')}</PanelTitle>
      <ProgressCardsColumn>
        {displayList.map((a, index) => (
          <React.Fragment key={a.id}>
            {index > 0 && <ProgressCardRowDivider />}
            <AchievementListRow
              achievement={a}
              percentage={a.is_completed ? 100 : (a.current_count / a.threshold) * 100}
              context="account"
              isCompleted={a.is_completed}
            />
          </React.Fragment>
        ))}
      </ProgressCardsColumn>
    </PanelWrapper>
  );
};
